import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { getFounderImageUrl } from '../utils/imageUtils';
import { GraduationCap, Award, Users, BookOpen, ArrowRight, Check } from 'lucide-react';

const About = () => {
  const [founder, setFounder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/founder`)
      .then(res => {
        setFounder(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <GraduationCap className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Meet Your Instructor</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold text-white mb-4" data-testid="about-title">
            About the Founder
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Dedicated to transforming STEM education in Singapore
          </p>
        </motion.div>

        {/* Founder Profile */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-electric-cyan/20 rounded-3xl blur-3xl"></div>
              <div className="glass-card p-4 relative">
                <img
                  src={getFounderImageUrl(founder)}
                  alt={founder?.name}
                  className="w-full aspect-square object-cover rounded-2xl"
                  onError={(e) => {
                    console.error('Founder image failed to load:', e.target.src);
                    e.target.onerror = null;
                    // Fallback to legacy image_url or default
                    if (founder?.image_url && e.target.src !== founder.image_url) {
                      e.target.src = founder.image_url;
                    } else {
                      e.target.src = 'https://images.unsplash.com/photo-1665111912062-87bde7daedc7?w=400';
                    }
                  }}
                  data-testid="founder-image"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
              {founder?.name || 'Jagalpure Mayur Manoj'}
            </h2>
            <p className="text-electric-cyan text-lg mb-2">
              {founder?.title || 'Founder & Lead Instructor'}
            </p>
            <p className="text-slate-400 font-medium mb-6">
              {founder?.credentials || 'Bachelors @ IIT Hyderabad'}
            </p>
            
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              {founder?.bio || "With a passion for STEM education and a mission to make complex concepts accessible, I founded PhysicsCo to help students across Singapore excel in their academic journey."}
            </p>

            {/* Achievements */}
            <div className="space-y-3 mb-8">
              {(founder?.achievements || [
                'IIT Hyderabad Graduate',
                '10+ Years Teaching Experience',
                '500+ Students Mentored',
                '95% Success Rate'
              ]).map((achievement, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-electric-cyan/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-electric-cyan" />
                  </div>
                  <span className="text-slate-300">{achievement}</span>
                </div>
              ))}
            </div>

            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="about-contact-btn">
              Get in Touch <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        {/* Mission & Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Our Mission & Values
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: BookOpen,
                title: 'Excellence in Education',
                description: 'We strive for the highest standards in STEM education, ensuring every student receives world-class instruction.'
              },
              {
                icon: Users,
                title: 'Personalized Attention',
                description: 'Every student is unique. We tailor our teaching approach to match individual learning styles and goals.'
              },
              {
                icon: Award,
                title: 'Results-Driven',
                description: 'Our focus is on tangible outcomes. We measure success by the achievements of our students.'
              }
            ].map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-6 text-center card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-electric-cyan/20 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-electric-cyan" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-white mb-2">{value.title}</h3>
                <p className="text-slate-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Boards We Cover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Boards We Specialize In
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Comprehensive coverage of all major international and local curricula
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {['IB', 'IGCSE', 'A-Level', 'O-Level', 'CBSE', 'ICSE'].map((board, idx) => (
              <motion.div
                key={board}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card p-6 text-center card-hover"
              >
                <GraduationCap className="w-8 h-8 text-electric-cyan mx-auto mb-2" />
                <span className="font-outfit font-semibold text-white">{board}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-12 text-center mt-24 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-electric-cyan to-transparent"></div>
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Book a free consultation to discuss your academic goals and how we can help you achieve them.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register" className="btn-primary flex items-center gap-2">
              Register Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/courses" className="btn-secondary">
              View Courses
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
